import { Currency } from "../../submodules/rapdv/server/types/Currency"
import { Money } from "../../submodules/rapdv/server/types/Money"

export type BasketAddon = {
  name: string
  price: number
  quantity: number
}

export type BasketItem = {
  dishId?: string
  halfAndHalf?: [string, string]
  addons?: BasketAddon[]
  count: number
}

export type Basket = {
  items: BasketItem[]
}

export enum DishFeeType {
  Fixed      = "fixed",
  Percentage = "percentage",
}

export type DishFeeInfo = {
  name: string
  type: DishFeeType
  price: number
  percentage: number
  deliveryMethods: string[]
}

export type MenuAddon = {
  name: string
  price: number
}

export type DishInfo = {
  name: string
  price: number
  available?: boolean
  fees: DishFeeInfo[]
  addons?: MenuAddon[]
  categoryAddons?: MenuAddon[]
}

export type MenuData = {
  storeKey: string
  currency: string
  deliveryMethods: string[]
  isStoreOpen?: boolean
  dishes: Record<string, DishInfo>
}

export class Menu {

  private static readonly PERCENT_DIVISOR = 100
  private static readonly HALF_DIVISOR = 2
  private static readonly HALF_AND_HALF_KEY_PREFIX = "hh:"

  public menuData: MenuData

  constructor(menuData: MenuData) {
    this.menuData = menuData
  }

  public formatPrice = (readableAmount: number): string => {
    return Currency.showPriceAsText(Money.toSafe(readableAmount), this.menuData.currency, true)
  }

  public isFeeApplicable = (fee: DishFeeInfo, deliveryMethod: string | null): boolean => {
    if (deliveryMethod) {
      return fee.deliveryMethods.includes(deliveryMethod)
    } 
    const areAllDeliveryMethodsSupported = this.menuData.deliveryMethods.every((method) => fee.deliveryMethods.includes(method))
    return areAllDeliveryMethodsSupported
  }

  public getApplicableFees = (dish: DishInfo, deliveryMethod: string | null): DishFeeInfo[] => {
    return (dish.fees ?? []).filter((fee) => this.isFeeApplicable(fee, deliveryMethod))
  }

  private static dedupeByName = <T extends { name: string }>(entries: T[]): T[] => {
    const seen = new Set<string>()
    return entries.filter((entry) => {
      if (seen.has(entry.name)) return false
      seen.add(entry.name)
      return true
    })
  }

  public getItemApplicableFees = (item: BasketItem, deliveryMethod: string | null): DishFeeInfo[] => {
    const halves = this.getHalfAndHalfDishes(item)
    if (halves) {
      return Menu.dedupeByName([
        ...this.getApplicableFees(halves[0], deliveryMethod),
        ...this.getApplicableFees(halves[1], deliveryMethod),
      ])
    }
    const dish = this.menuData.dishes[item.dishId ?? ""]
    return dish ? this.getApplicableFees(dish, deliveryMethod) : []
  }

  private getItemFeeUnitAmountSafe = (item: BasketItem, fee: DishFeeInfo): number =>
    fee.type === DishFeeType.Fixed
      ? Money.toSafe(fee.price)
      : Math.round(this.getItemUnitPriceSafe(item) * fee.percentage / Menu.PERCENT_DIVISOR)

  public getItemFeeLineAmount = (item: BasketItem, fee: DishFeeInfo, count: number): number =>
    Money.toReadable(this.getItemFeeUnitAmountSafe(item, fee) * count)

  public getItemKey = (item: BasketItem): string =>
    item.halfAndHalf
      ? `${Menu.HALF_AND_HALF_KEY_PREFIX}${[...item.halfAndHalf].sort().join("+")}${this.getAddonsSignature(item)}`
      : `${item.dishId ?? ""}${this.getAddonsSignature(item)}`

  private getAddonsSignature = (item: BasketItem): string => {
    const addons = item.addons ?? []
    if (addons.length === 0) return ""
    const parts = addons.map((addon) => `${addon.name}~${addon.price}~${addon.quantity}`).sort()
    return `|${parts.join(",")}`
  }

  public getItemAddons = (item: BasketItem): BasketAddon[] => item.addons ?? []

  public getAddonLineAmount = (addon: BasketAddon): number =>
    Money.toReadable(Money.toSafe(addon.price) * addon.quantity)

  public getDishAddons = (dishId: string): MenuAddon[] => this.menuData.dishes[dishId]?.addons ?? []

  public getCategoryAddons = (dishId: string): MenuAddon[] => this.menuData.dishes[dishId]?.categoryAddons ?? []

  public dishHasAddons = (dishId: string): boolean =>
    this.getDishAddons(dishId).length > 0 || this.getCategoryAddons(dishId).length > 0

  public getHalfAndHalfDishAddons = (firstDishId: string, secondDishId: string): MenuAddon[] =>
    Menu.dedupeByName([...this.getDishAddons(firstDishId), ...this.getDishAddons(secondDishId)])

  public getHalfAndHalfCategoryAddons = (firstDishId: string, secondDishId: string): MenuAddon[] =>
    Menu.dedupeByName([...this.getCategoryAddons(firstDishId), ...this.getCategoryAddons(secondDishId)])

  public halfAndHalfHasAddons = (firstDishId: string, secondDishId: string): boolean =>
    this.getHalfAndHalfDishAddons(firstDishId, secondDishId).length > 0
    || this.getHalfAndHalfCategoryAddons(firstDishId, secondDishId).length > 0

  private getAddonsPerUnitSafe = (item: BasketItem): number =>
    (item.addons ?? []).reduce((sum, addon) => sum + Money.toSafe(addon.price) * addon.quantity, 0)

  public getHalfAndHalfDishes = (item: BasketItem): [DishInfo, DishInfo] | null => {
    if (!item.halfAndHalf) return null
    const firstDish = this.menuData.dishes[item.halfAndHalf[0]]
    const secondDish = this.menuData.dishes[item.halfAndHalf[1]]
    if (!firstDish || !secondDish) return null
    return [firstDish, secondDish]
  }

  public getItemName = (item: BasketItem): string => {
    const halves = this.getHalfAndHalfDishes(item)
    if (halves) return `1/2 ${halves[0].name} + 1/2 ${halves[1].name}`
    return this.menuData.dishes[item.dishId ?? ""]?.name ?? ""
  }

  private getItemUnitPriceSafe = (item: BasketItem): number => {
    const halves = this.getHalfAndHalfDishes(item)
    if (halves) {
      return Math.round(Money.toSafe(halves[0].price) / Menu.HALF_DIVISOR)
        + Math.round(Money.toSafe(halves[1].price) / Menu.HALF_DIVISOR)
    }
    return Money.toSafe(this.menuData.dishes[item.dishId ?? ""]?.price ?? 0)
  }

  public getItemUnitPrice = (item: BasketItem): number => Money.toReadable(this.getItemUnitPriceSafe(item))

  public isItemAvailable = (item: BasketItem): boolean => {
    if (item.halfAndHalf) {
      const halves = this.getHalfAndHalfDishes(item)
      return !!halves && halves[0].available !== false && halves[1].available !== false
    }
    const dish = this.menuData.dishes[item.dishId ?? ""]
    return !!dish && dish.available !== false
  }

  public itemExists = (item: BasketItem): boolean =>
    item.halfAndHalf ? !!this.getHalfAndHalfDishes(item) : !!this.menuData.dishes[item.dishId ?? ""]

  private getItemLineTotalSafe = (item: BasketItem, deliveryMethod: string | null): number => {
    if (!this.itemExists(item)) return 0
    let lineSafe = this.getItemUnitPriceSafe(item) * item.count
    for (const fee of this.getItemApplicableFees(item, deliveryMethod)) {
      lineSafe += this.getItemFeeUnitAmountSafe(item, fee) * item.count
    }
    return lineSafe + this.getAddonsPerUnitSafe(item) * item.count
  }

  public getItemLineTotal = (item: BasketItem, deliveryMethod: string | null): number =>
    Money.toReadable(this.getItemLineTotalSafe(item, deliveryMethod))

  public getSubtotal = (basket: Basket, deliveryMethod: string | null): number => {
    let subtotalSafe = 0
    for (const item of basket.items) {
      subtotalSafe += this.getItemLineTotalSafe(item, deliveryMethod)
    }
    return Money.toReadable(subtotalSafe)
  }

  public static getStorageKeyFor = (storeKey: string) => `restanaut-basket-${storeKey}`

  public getStorageKey = () => Menu.getStorageKeyFor(this.menuData.storeKey)

  public load = (): Basket => {
    try {
      const storageKey = this.getStorageKey()
      
      const raw = localStorage.getItem(storageKey)
      if (!raw) return { items: [] }

      const parsed = JSON.parse(raw)
      if (!parsed || !Array.isArray(parsed.items)) { 
        return { items: [] }
      }
      
      const items: BasketItem[] = parsed.items
        .filter((item: any) => item && Number(item.count) > 0)
        .map((item: any) => this.normalizeStoredItem(item))
        .filter((item: BasketItem | null): item is BasketItem => item !== null && this.itemExists(item))
      return { items }
    } catch (error) {
      return { items: [] }
    }
  }

  private normalizeStoredAddons = (rawAddons: any): BasketAddon[] => {
    if (!Array.isArray(rawAddons)) return []
    return rawAddons
      .filter((addon) => addon && typeof addon.name === "string" && Number(addon.quantity) > 0)
      .map((addon) => ({ name: addon.name, price: Number(addon.price) || 0, quantity: Math.max(1, Math.floor(Number(addon.quantity))) }))
  }

  private normalizeStoredItem = (item: any): BasketItem | null => {
    const count = Math.floor(Number(item.count))
    const addons = this.normalizeStoredAddons(item.addons)
    if (Array.isArray(item.halfAndHalf) && item.halfAndHalf.length === 2
      && typeof item.halfAndHalf[0] === "string" && typeof item.halfAndHalf[1] === "string") {
      return { halfAndHalf: [item.halfAndHalf[0], item.halfAndHalf[1]], count, ...(addons.length > 0 && { addons }) }
    }
    if (typeof item.dishId === "string") {
      return { dishId: item.dishId, count, ...(addons.length > 0 && { addons }) }
    }
    return null
  }

  public save = (basket: Basket): void => {
    const storageKey = this.getStorageKey()
    localStorage.setItem(storageKey, JSON.stringify(basket))
  }

}